import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
  Button,
  IconButton,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CameraAlt as CameraIcon,
  ArrowBack as BackIcon,
  PhotoCamera as PhotoCameraIcon,
  // FlipCameraIos as FlipCameraIcon, // TODO: Re-enable when camera switch is fixed
} from '@mui/icons-material';
import { useNavigate } from '@tanstack/react-router';
import { DocumentUpload, type UploadedDocument, type DocumentUploadRef } from '../components/documents/DocumentUpload';
import { DocumentType } from '@ata-digital/client-tax-system-api-client';
import { useSequentialBatchUpload } from '../hooks/api/useSequentialBatchUpload';
import { createUploadBatches, validateTotalSize } from '../utils/uploadBatcher';
import { getCameraErrorMessage, checkCameraPermission } from '../utils/browserUtils';
import { createLogger } from '../utils/logger';
import { QueryErrorBoundary } from '../components/query/QueryErrorBoundary';

const log = createLogger('UploadPage');

// TODO: Refactor to use DocumentUpload component instead of inline camera logic to reduce duplication

export const Route = createFileRoute('/upload')({
  component: UploadPage,
  errorComponent: QueryErrorBoundary,
  validateSearch: (
    search: Record<string, unknown>
  ): {
    replace?: string;
    type?: string;
    returnTo?: string;
    binderId?: string;
  } => {
    return {
      replace: (search.replace as string) || undefined,
      type: (search.type as string) || undefined,
      returnTo: (search.returnTo as string) || undefined,
      binderId: (search.binderId as string) || undefined,
    };
  },
});

function UploadPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get document info from search params if replacing
  const searchParams = Route.useSearch();
  const isReplacing = !!searchParams?.replace;
  const preselectedType = searchParams?.type;
  const returnTo = searchParams?.returnTo || '/tax-binders'; // Default to binders list if no return path
  const binderId = searchParams?.binderId; // Get binderId from search params (Issue #33)

  // State for upload mode - default to camera on mobile, upload on desktop
  const [uploadMode, setUploadMode] = useState<'upload' | 'camera'>(isMobile ? 'camera' : 'upload');
  const [documentType, setDocumentType] = useState<DocumentType>(
    (preselectedType as DocumentType) || DocumentType.OtherClientDocument
  );
  const [uploadedFiles, setUploadedFiles] = useState<UploadedDocument[]>([]);
  const [comment, setComment] = useState<string>('');
  const [pendingCount, setPendingCount] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Ref to control DocumentUpload component
  const documentUploadRef = useRef<DocumentUploadRef>(null);

  // Camera state (must be declared before any conditional returns)
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [facingMode, _setFacingMode] = useState<'user' | 'environment'>('environment'); // TODO: Re-enable when camera switch is fixed
  const [isLoadingCamera, setIsLoadingCamera] = useState(false);

  // API hook for uploading documents in batches with progress (Issue #33)
  const { uploadBatches, progress: uploadProgress, isUploading: isBatchUploading } = useSequentialBatchUpload();

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      setIsLoadingCamera(true);
      setCameraError(null);

      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Log permission state for debugging, but don't short-circuit on 'denied'
      // Android Permissions API can return 'denied' even when user was never asked
      const permissionState = await checkCameraPermission();
      log.debug('Camera permission state:', permissionState);

      // Start with minimal constraints, then apply preferences
      // Complex constraints can cause getUserMedia to fail on some Android devices
      let stream: MediaStream;
      try {
        // Try with preferred constraints first
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: facingMode,
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
      } catch (constraintErr) {
        log.debug('Preferred constraints failed, trying minimal:', constraintErr);
        // Fallback to minimal constraints
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
      }
    } catch (err) {
      log.error('Failed to start camera:', err);
      setCameraError(getCameraErrorMessage(err));
    } finally {
      setIsLoadingCamera(false);
    }
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  }, []);

  // Update upload mode when screen size changes
  useEffect(() => {
    if (!uploadedFiles.length) {
      setUploadMode(isMobile ? 'camera' : 'upload');
    }
  }, [isMobile, uploadedFiles.length]);

  // Start camera when camera mode is selected
  useEffect(() => {
    if (uploadMode === 'camera' && !cameraActive && !capturedImage) {
      startCamera();
    } else if (uploadMode === 'upload' && cameraActive) {
      stopCamera();
    }
  }, [uploadMode, cameraActive, capturedImage, startCamera, stopCamera]);

  // Cleanup camera on unmount only
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // TODO: Fix camera switch race condition before re-enabling
  // const switchCamera = async () => {
  //   const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
  //   setFacingMode(newFacingMode);
  //   await startCamera();
  // };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);

        // Use canvas.toBlob() for reliable blob creation (works better on mobile)
        canvas.toBlob(
          (blob) => {
            if (blob) {
              setCapturedBlob(blob);
              // Create data URL only for preview display
              const imageData = canvas.toDataURL('image/jpeg', 0.9);
              setCapturedImage(imageData);
              stopCamera();
            } else {
              log.error('Failed to capture photo: toBlob returned null');
              setCameraError('Failed to capture photo. Please try again.');
            }
          },
          'image/jpeg',
          0.9
        );
      }
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setCapturedBlob(null);
    startCamera();
  };

  const handleCameraCapture = async (blob: Blob, previewUrl: string) => {
    setUploadError(null);

    try {
      // Create file directly from blob (no data URL conversion needed)
      const fileName = `camera-capture-${Date.now()}.jpg`;
      const file = new File([blob], fileName, { type: 'image/jpeg' });

      const doc: UploadedDocument = {
        id: `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        preview: previewUrl,
        type: documentType,
        status: 'pending',
      };

      setUploadedFiles((prev) => [...prev, doc]);

      // Upload to API using batched upload (Issue #33)
      const result = await uploadBatches({
        files: [file],
        binderId: binderId!, // Safe: guard clause ensures binderId is defined
        type: documentType,
        comment: comment.trim() || undefined,
      });

      if (result.failed > 0) {
        throw new Error(result.errors.join('\n'));
      }

      // Navigate back immediately after successful upload
      navigate({ to: returnTo });
    } catch (err: unknown) {
      log.error('Failed to process or upload captured image:', err);

      // Extract error message from API response
      let errorMessage = 'Upload failed. Please try again.';
      if (err && typeof err === 'object') {
        const error = err as {
          response?: { data?: { error?: { message?: string }; message?: string } };
          message?: string;
        };
        log.error('Error response:', error?.response?.data);

        if (error?.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
      }

      setUploadError(errorMessage);

      // Clear the captured image/blob so user can retake
      setCapturedImage(null);
      setCapturedBlob(null);
    }
  };

  const handleUpload = async (documents: UploadedDocument[]) => {
    setUploadError(null);
    setUploadedFiles((prev) => [...prev, ...documents]);

    try {
      const files = documents.map((doc) => doc.file);

      // Validate total size before uploading
      const validation = validateTotalSize(files);
      if (!validation.valid) {
        setUploadError(validation.error!);
        setUploadedFiles((prev) => prev.filter((f) => !documents.some((doc) => doc.id === f.id)));
        throw new Error(validation.error);
      }

      // Log batch info for debugging
      const batches = createUploadBatches(files);
      log.debug('Starting batched upload:', {
        fileCount: files.length,
        batchCount: batches.length,
        batches: batches.map((b, i) => ({
          index: i,
          fileCount: b.files.length,
          isPdfOrImage: b.isPdfOrImageBatch,
          fileNames: b.files.map((f) => f.name),
        })),
      });

      // Upload to API using batched upload (Issue #33)
      const result = await uploadBatches({
        files,
        binderId: binderId!, // Safe: guard clause ensures binderId is defined
        type: documentType,
        comment: comment.trim() || undefined,
      });

      // If all batches failed, treat as complete failure
      if (result.failed > 0 && result.successful === 0) {
        throw new Error(result.errors.join('\n'));
      }

      // If some batches failed but others succeeded, show error and don't navigate
      if (result.failed > 0 && result.successful > 0) {
        log.warn('Some batches failed:', result.errors);
        const partialErrorMessage =
          result.errors.length > 0
            ? `Some files failed to upload:\n${result.errors.join('\n')}`
            : 'Some files failed to upload. Please review and try again.';
        setUploadError(partialErrorMessage);
        // Don't navigate - let user see which files failed
        return;
      }

      // Navigate back only when all batches succeeded
      navigate({ to: returnTo });
    } catch (err: unknown) {
      log.error('Failed to upload documents:', err);

      // Extract error message from API response
      let errorMessage = 'Upload failed. Please try again.';
      if (err && typeof err === 'object') {
        const error = err as {
          response?: { data?: { error?: { message?: string }; message?: string } };
          message?: string;
        };
        log.error('Error response:', error?.response?.data);

        if (error?.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
      }

      setUploadError(errorMessage);

      // Remove the documents from uploadedFiles since they failed
      setUploadedFiles((prev) => prev.filter((f) => !documents.some((doc) => doc.id === f.id)));

      // Re-throw the error so DocumentUpload component knows it failed
      throw err;
    }
  };

  const handleDelete = (documentId: string) => {
    log.debug('Document deleted:', documentId);
    setUploadedFiles((prev) => prev.filter((f) => f.id !== documentId));
  };

  const handleModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'upload' | 'camera' | null) => {
    if (newMode !== null) {
      setUploadMode(newMode);
    }
  };

  const handleBack = () => {
    navigate({ to: returnTo });
  };

  // Show error if binderId is missing (after all hooks are called)
  if (!binderId) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Missing Binder ID
          </Typography>
          <Typography variant="body2">
            This page requires a binder ID. Please navigate here from a binder page.
          </Typography>
          <Button variant="outlined" onClick={() => navigate({ to: '/tax-binders' })} sx={{ mt: 2 }}>
            Go to Binders
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton onClick={handleBack} edge="start">
          <BackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {isReplacing ? 'Replace Document' : 'Upload Document'}
        </Typography>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={3}>
          {/* Upload Mode Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={uploadMode}
              exclusive
              onChange={handleModeChange}
              aria-label="upload mode"
              size={isMobile ? 'small' : 'medium'}
            >
              <ToggleButton value="upload" aria-label="file upload">
                <UploadIcon sx={{ mr: 1 }} />
                File Upload
              </ToggleButton>
              <ToggleButton value="camera" aria-label="camera capture">
                <CameraIcon sx={{ mr: 1 }} />
                Camera Capture
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Document Type Selection */}
          <FormControl fullWidth>
            <InputLabel>Document Type</InputLabel>
            <Select
              value={documentType}
              label="Document Type"
              onChange={(e) => setDocumentType(e.target.value as DocumentType)}
              disabled={isReplacing}
            >
              <MenuItem value={DocumentType.TaxReturn}>Tax Return</MenuItem>
              <MenuItem value={DocumentType.IncomeDocument}>Income Document (W-2, 1099, etc.)</MenuItem>
              <MenuItem value={DocumentType.DeductionDocument}>Deduction Document</MenuItem>
              <MenuItem value={DocumentType.SupportingDocument}>Supporting Document</MenuItem>
              <MenuItem value={DocumentType.IdentificationDocument}>Identification Document</MenuItem>
              <MenuItem value={DocumentType.EFileAuthorization}>E-File Authorization</MenuItem>
              <MenuItem value={DocumentType.TaxBinder}>Tax Binder</MenuItem>
              <MenuItem value={DocumentType.EngagementLetter}>Engagement Letter</MenuItem>
              <MenuItem value={DocumentType.PowerOfAttorney}>Power of Attorney</MenuItem>
              <MenuItem value={DocumentType.OtherClientDocument}>Other</MenuItem>
            </Select>
          </FormControl>

          {/* Optional Comment Field */}
          <TextField
            fullWidth
            label="Comment (Optional)"
            placeholder="Add any notes or comments about this document"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            rows={3}
            helperText="This comment will be visible to your tax preparer"
            variant="outlined"
          />

          {/* Upload Error Alert */}
          {uploadError && (
            <Alert severity="error" onClose={() => setUploadError(null)}>
              <strong>Upload Failed:</strong> {uploadError}
            </Alert>
          )}

          {/* Upload Component or Camera View */}
          {uploadMode === 'upload' ? (
            <DocumentUpload
              ref={documentUploadRef}
              documentType={documentType}
              allowCamera={false}
              allowMultiple={!isReplacing}
              autoUpload={false}
              showPreviews={true}
              maxFiles={isReplacing ? 1 : 50}
              onUpload={handleUpload}
              onDelete={handleDelete}
              hideInternalActions={true}
              onPendingChange={setPendingCount}
            />
          ) : (
            <Box sx={{ position: 'relative', width: '100%' }}>
              {/* Camera View */}
              {!capturedImage ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '133%', // 3:4 portrait aspect ratio for mobile camera
                    backgroundColor: '#000',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  {/* Video element always rendered so ref is available for stream attachment */}
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      // Hide video during loading or error, but keep it in DOM
                      visibility: !isLoadingCamera && !cameraError ? 'visible' : 'hidden',
                    }}
                  />

                  {/* Loading spinner overlay */}
                  {isLoadingCamera && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CircularProgress color="primary" />
                    </Box>
                  )}

                  {/* Error overlay */}
                  {cameraError && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 3,
                      }}
                    >
                      <Alert severity="error">{cameraError}</Alert>
                    </Box>
                  )}

                  {/* Camera UI elements - only show when camera is active and no error */}
                  {!isLoadingCamera && !cameraError && (
                    <>
                      {/* Document Viewfinder Overlay - Removed because it doesn't match actual capture area */}
                      {/* <Box
                        sx={{
                          position: 'absolute',
                          top: '10%',
                          left: '5%',
                          right: '5%',
                          bottom: '10%',
                          border: '3px solid',
                          borderColor: 'primary.main',
                          borderRadius: 2,
                          pointerEvents: 'none',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -3,
                            left: -3,
                            width: 30,
                            height: 30,
                            borderTop: '4px solid',
                            borderLeft: '4px solid',
                            borderColor: 'primary.light',
                            borderRadius: '8px 0 0 0',
                          },
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: -3,
                            right: -3,
                            width: 30,
                            height: 30,
                            borderTop: '4px solid',
                            borderRight: '4px solid',
                            borderColor: 'primary.light',
                            borderRadius: '0 8px 0 0',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: -3,
                            left: -3,
                            width: 30,
                            height: 30,
                            borderBottom: '4px solid',
                            borderLeft: '4px solid',
                            borderColor: 'primary.light',
                            borderRadius: '0 0 0 8px',
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: -3,
                            right: -3,
                            width: 30,
                            height: 30,
                            borderBottom: '4px solid',
                            borderRight: '4px solid',
                            borderColor: 'primary.light',
                            borderRadius: '0 0 8px 0',
                          }}
                        />
                      </Box> */}

                      {/* Camera Controls */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 20,
                          left: 0,
                          right: 0,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 3,
                        }}
                      >
                        {/* Switch Camera Button - TODO: Fix camera toggle race condition */}
                        {/* <IconButton
                          onClick={switchCamera}
                          sx={{
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.3)',
                            },
                          }}
                        >
                          <FlipCameraIcon />
                        </IconButton> */}

                        {/* Capture Button */}
                        <IconButton
                          onClick={capturePhoto}
                          sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            width: 56,
                            height: 56,
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                          }}
                        >
                          <PhotoCameraIcon sx={{ fontSize: 28 }} />
                        </IconButton>

                        {/* Placeholder for symmetry - removed since camera toggle is hidden */}
                        {/* <Box sx={{ width: 48 }} /> */}
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Box sx={{ position: 'relative', width: '100%' }}>
                  <img
                    src={capturedImage}
                    alt="Captured document"
                    style={{
                      width: '100%',
                      borderRadius: 8,
                    }}
                  />
                  <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      onClick={retakePhoto}
                      startIcon={<CameraIcon />}
                      disabled={isBatchUploading}
                    >
                      Retake
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleCameraCapture(capturedBlob!, capturedImage!)}
                      startIcon={isBatchUploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
                      disabled={isBatchUploading || !capturedBlob}
                    >
                      {isBatchUploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Hidden canvas for capture */}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </Box>
          )}

          {/* Tips Section - Show relevant tips based on device */}
          {isMobile ? (
            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                Tips for Mobile:
              </Typography>
              <Typography variant="body2" component="div">
                {uploadMode === 'camera' ? (
                  <>
                    • Hold your device steady for clear photos
                    <br />
                    • Ensure good lighting on the document
                    <br />
                    • Frame the entire document in view
                    <br />• Tap to focus if the image is blurry
                  </>
                ) : (
                  <>
                    • Tap the upload area to select files
                    <br />
                    • You can select photos from your gallery
                    <br />
                    • PDFs and images are supported
                    <br />• Files must be under 10MB each
                  </>
                )}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                Tips for Desktop:
              </Typography>
              <Typography variant="body2" component="div">
                {uploadMode === 'camera' ? (
                  <>
                    • Position document flat in front of webcam
                    <br />
                    • Ensure adequate lighting
                    <br />
                    • Click capture when document is in focus
                    <br />• Use a webcam with good resolution for best results
                  </>
                ) : (
                  <>
                    • Drag and drop files directly onto the upload area
                    <br />
                    • Or click to browse and select files
                    <br />
                    • Select multiple files at once (Ctrl/Cmd + Click)
                    <br />• Supported formats: PDF, JPG, PNG (max 10MB each)
                  </>
                )}
              </Typography>
            </Alert>
          )}
        </Stack>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button variant="outlined" onClick={handleBack} size="large" sx={{ minWidth: 150 }}>
          Cancel
        </Button>
        {uploadMode === 'upload' && pendingCount > 0 && (
          <Button
            variant="contained"
            onClick={() => documentUploadRef.current?.uploadAll()}
            size="large"
            sx={{ minWidth: 150 }}
            disabled={isBatchUploading}
          >
            {isBatchUploading ? `Uploading... ${uploadProgress?.overallProgress ?? 0}%` : `Upload (${pendingCount})`}
          </Button>
        )}
      </Box>
    </Box>
  );
}
